if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInputs.forEach((input) => {
          input.disabled = false;
        });
        this.id = this.form.querySelector('[name="id"]')?.value;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.sellingPlanSelect = this.form.querySelector('[id*="ProductSellingPlan-"]');
        this.sellingPlanInput = this.form.querySelector('[name="selling_plan"]');
        this.quantityInput = this.form.querySelector('[name="quantity"]');
        this.acceleratedCheckout = this.form.querySelector('[data-accelerated-checkout');
        this.handle = this.dataset.handle;
        this.dynamicBoxId = null;
        this.stackParentId = this.dataset.stackParentId;
        this.stackProducts = this.dataset.stackProducts;
        this.stackSellingPlan = this.dataset.stackSellingPlan;

        if (this.acceleratedCheckout) {
          this.acceleratedCheckout.addEventListener('click', this.onSubmitAcceleratedCheckout.bind(this));
        }

        if (this.sellingPlanSelect)
          this.sellingPlanSelect.addEventListener('change', this.onSellingPlanChange.bind(this));

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        if (this.stackParentId) {
          this.fetchDynamicBox(this.stackParentId);
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSellingPlanChange(evt) {
        const selectedOption = evt.target.selectedOptions[0];
        const id = selectedOption.getAttribute('data-selling-plan-id');
        if (this.sellingPlanInput) this.sellingPlanInput.value = id;
      }

      fetchDynamicBox(id) {
        fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            productPlatformId: id,
          }),
        })
          .then((response) => response.json())
          .then((response) => {
            this.dynamicBoxId = response.DynamicBox.boxId;
          });
      }

      getRequestConfig(formData) {
        const sections = this.cart.getSectionsToRender().map((section) => section.id);

        // Default case when no gift with purchase
        if (this.cart) {
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        return {
          ...fetchConfig('javascript'),
          body: formData,
        };
      }

      onSubmitAcceleratedCheckout(evt) {
        evt.preventDefault();
        evt.target.disabled = true;

        const formData = new FormData(this.form);
        const config = this.getRequestConfig(formData);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            window.location.href = '/checkout';
          });
      }

      getStackRequestConfig(quantity) {
        const sections = this.cart.getSectionsToRender().map((section) => section.id);
        const products = this.stackProducts.split(',');
        const boxDate = new Date().toISOString();
        const sellingPlan = this.stackSellingPlan;
        const dynamicBoxId = this.stackSellingPlan ? this.dynamicBoxId : undefined;

        const items = products.map((id) => ({
          ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
          id: parseInt(id, 10),
          quantity,
          properties: {
            ...(dynamicBoxId ? { _dynamicBoxId: dynamicBoxId } : {}),
            _dynamicBoxIndex: 0,
            _dynamicBoxDate: boxDate,
            _dynamicBoxHandle: this.handle,
          },
        }));

        return {
          ...fetchConfig(),
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname,
          }),
        };
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.dynamicBoxId === null && this.dataset.stackProducts) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        const formData = new FormData(this.form);
        const quantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) : 1;

        const config = this.dataset.stackProducts
          ? this.getStackRequestConfig(quantity)
          : this.getRequestConfig(formData);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'add_to_cart',
              ecommerce: {
                items: [
                  {
                    item_name: response.title,
                    item_id: response.id,
                    quantity: response.quantity,
                  },
                ],
              },
              add_to_cart_source: 'product_form',
            });

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInputs() {
        return this.form.querySelectorAll('[name^=id]');
      }
    }
  );
}
